import { useEffect, useState } from "react";

/**
 * Each build publishes a new hashed entry script (`/assets/index-<hash>.js`). A tab left open on
 * an iPad keeps running the build it loaded until someone reloads it, and silently saves in that
 * build's older format (HS of Rapid City, 10/20/2026: ROM videos with no movement, no Get Up, no
 * Shoulder Abduction). Comparing the loaded entry with the one the site serves now catches that.
 */
const ENTRY_SCRIPT = /\/assets\/index-[\w-]+\.js/;
/** Only a `<script src>`: a lazy chunk can also be named `index-*.js` (e.g. a preload link). */
const ENTRY_SCRIPT_TAG = /<script\b[^>]*\ssrc="[^"]*?(\/assets\/index-[\w-]+\.js)"/;
const CHECK_EVERY_MS = 5 * 60_000;

/** The entry script path in a script URL; null when it is not one. */
export function entryScriptFrom(url: string): string | null {
  return url.match(ENTRY_SCRIPT)?.[0] ?? null;
}

/** The entry script path the served page loads; null when there is none. */
export function entryScriptInHtml(html: string): string | null {
  return html.match(ENTRY_SCRIPT_TAG)?.[1] ?? null;
}

/** True only when both builds are known and differ: unknown never nags. */
export function isNewerBuild(loaded: string | null, published: string | null): boolean {
  return loaded != null && published != null && loaded !== published;
}

/** Null in local development, which serves unhashed source and has nothing to compare. */
function loadedEntryScript(): string | null {
  for (const s of Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))) {
    const hit = entryScriptFrom(s.src);
    if (hit) return hit;
  }
  return null;
}

async function publishedEntryScript(): Promise<string | null> {
  const res = await fetch(`/?build-check=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) return null;
  return entryScriptInHtml(await res.text());
}

/** Turns true once the site is serving a newer build than this tab is running. */
export function useNewerBuildAvailable(): boolean {
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    const loaded = loadedEntryScript();
    if (!loaded) return;
    let stopped = false;
    // An iPad woken after days asleep shows its old page first: check the moment it is looked at.
    // Safari restoring a page from its back-forward cache fires `pageshow`, not always the others.
    const WAKE_EVENTS = ["focus", "online", "pageshow"] as const;

    const stop = () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
      for (const ev of WAKE_EVENTS) window.removeEventListener(ev, check);
    };

    const check = async () => {
      if (stopped || document.visibilityState !== "visible") return;
      try {
        if (isNewerBuild(loaded, await publishedEntryScript()) && !stopped) {
          setNewer(true);
          stop(); // Nothing left to find out until the page reloads.
        }
      } catch {
        // Offline or a blip: the next check tries again.
      }
    };

    const timer = window.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", check);
    for (const ev of WAKE_EVENTS) window.addEventListener(ev, check);
    void check();
    return stop;
  }, []);

  return newer;
}
